import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, Clock, Layers, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import { STUDY_IMAGE } from "../lib/brand";
import { useAuth } from "../context/AuthContext";

const TCF_IMG = STUDY_IMAGE;

const DEFAULT_COURSES = [
  {
    course_id: "tcf_starter",
    slug: "tcf-starter",
    title_en: "TCF Canada — Starter Pack",
    title_fr: "TCF Canada — Pack Débutant",
    tagline_en: "Understand the exam. Build your foundation.",
    tagline_fr: "Comprendre l'examen. Bâtir vos bases.",
    description_en: "Everything you need to understand the TCF Canada: format, scoring, sample questions, and 6 hours of structured study material.",
    description_fr: "Tout ce qu'il faut pour comprendre le TCF Canada : format, notation, exemples de questions, et 6 heures de matériel d'étude structuré.",
    price: 49.00,
    currency: "CAD",
    duration_hours: 6,
    level: "Beginner",
    modules: [
      { title_en: "TCF Canada Overview", title_fr: "Aperçu du TCF Canada" },
      { title_en: "Listening Comprehension Basics", title_fr: "Compréhension orale — bases" },
      { title_en: "Reading Comprehension Basics", title_fr: "Compréhension écrite — bases" },
      { title_en: "Written Expression Intro", title_fr: "Expression écrite — intro" },
      { title_en: "Oral Expression Intro", title_fr: "Expression orale — intro" },
    ],
    order: 1,
  },
  {
    course_id: "tcf_complete",
    slug: "tcf-complete",
    title_en: "TCF Canada — Complete Prep",
    title_fr: "TCF Canada — Préparation Complète",
    tagline_en: "20+ hours to reach your target score.",
    tagline_fr: "20+ heures pour atteindre votre score cible.",
    description_en: "Full-length practice tests, live feedback on written expression, oral simulation exercises, and score-boosting strategies for all 4 sections.",
    description_fr: "Tests blancs complets, retour sur l'expression écrite, exercices d'oral simulé, et stratégies pour maximiser votre score dans les 4 sections.",
    price: 149.00,
    currency: "CAD",
    duration_hours: 22,
    level: "Intermediate to Advanced",
    modules: [
      { title_en: "All Starter Content", title_fr: "Tout le contenu Débutant" },
      { title_en: "Advanced Listening (with real audio)", title_fr: "Écoute avancée (audio réel)" },
      { title_en: "Reading Strategy Frameworks", title_fr: "Stratégies de lecture" },
      { title_en: "Written Expression Deep Dives", title_fr: "Expression écrite en profondeur" },
      { title_en: "Oral Expression Simulations", title_fr: "Simulations d'expression orale" },
      { title_en: "3 Full Mock Exams", title_fr: "3 examens blancs complets" },
      { title_en: "1-on-1 Feedback Session", title_fr: "Session de feedback 1-à-1" },
    ],
    order: 2,
  },
];

export default function TcfTraining() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [courses, setCourses] = useState(DEFAULT_COURSES);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api
      .get("/courses")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          setCourses(r.data);
        } else if (Array.isArray(r.data?.courses) && r.data.courses.length > 0) {
          setCourses(r.data.courses);
        }
      })
      .catch(() => {
        // Retain DEFAULT_COURSES
      });
  }, []);

  const enroll = async (course) => {
    if (!user) {
      window.location.href = "/login?redirect=/tcf";
      return;
    }
    setBusyId(course.course_id);
    try {
      const r = await api.post("/checkout", {
        product_id: course.course_id,
        kind: "course",
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      alert("Payment error. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-alabaster" data-testid="tcf-page">
      {/* HERO */}
      <section className="relative grid lg:grid-cols-12 min-h-[72vh] overflow-hidden">
        <div className="lg:col-span-7 bg-navy text-alabaster px-6 lg:px-20 py-20 flex flex-col justify-center">
          <p className="text-[10px] tracking-[0.35em] uppercase text-gold mb-4">{t("tcf.overline")}</p>
          <h1 className="font-serif text-5xl lg:text-7xl leading-[1.02] tracking-tight">
            {lang === "fr" ? "Le TCF Canada," : "TCF Canada,"}
            <br />
            <span className="italic text-terracotta">{lang === "fr" ? "démystifié." : "demystified."}</span>
          </h1>
          <p className="mt-8 max-w-lg text-alabaster/70 text-lg leading-relaxed">{t("tcf.sub")}</p>

          <div className="mt-10 grid grid-cols-3 gap-8 border-t border-alabaster/15 pt-8 max-w-md">
            <div>
              <p className="font-serif text-3xl text-gold">4</p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-alabaster/60 mt-1">{lang === "fr" ? "sections" : "sections"}</p>
            </div>
            <div>
              <p className="font-serif text-3xl text-terracotta">699</p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-alabaster/60 mt-1">{lang === "fr" ? "score max" : "max score"}</p>
            </div>
            <div>
              <p className="font-serif text-3xl">B2+</p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-alabaster/60 mt-1">CLB 7</p>
            </div>
          </div>

          <Link
            to="/tcf/diagnostic"
            data-testid="tcf-hero-diagnostic-cta"
            className="mt-10 inline-flex items-center gap-3 bg-gold text-navy px-6 py-3.5 rounded-full text-sm hover:bg-alabaster transition"
          >
            <span className="w-6 h-6 rounded-full bg-navy text-gold grid place-items-center text-xs">?</span>
            {lang === "fr" ? "Diagnostic gratuit — 10 min" : "Free diagnostic — 10 min"}
          </Link>
        </div>
        <div className="lg:col-span-5 relative min-h-[440px]">
          <img src={TCF_IMG} alt="Studying French" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent" />
        </div>
      </section>

      {/* COURSES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-4">— {t("tcf.section_modules")}</p>
            <h2 className="font-serif text-4xl lg:text-5xl text-navy tracking-tight">
              {lang === "fr" ? "Deux parcours. Un objectif." : "Two paths. One goal."}
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <p className="text-navy/70 text-lg leading-relaxed max-w-xl">
              {lang === "fr"
                ? "Choisissez le pack qui correspond à votre niveau. Commencez par le Débutant si vous découvrez le TCF, ou plongez dans le Complet si vous visez CLB 9+."
                : "Pick the pack that matches your level. Start with Starter if TCF is new to you, or dive into Complete if you're aiming for CLB 9+."}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {(Array.isArray(courses) ? courses : DEFAULT_COURSES).map((c, idx) => (
            <motion.div
              key={c.course_id || `course-${idx}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: idx * 0.1 }}
              data-testid={`course-card-${c.course_id}`}
              className={`relative bg-white rounded-2xl border overflow-hidden ${
                idx === 1 ? "border-terracotta/40 shadow-xl" : "border-navy/8"
              }`}
            >
              {idx === 1 && (
                <div className="absolute top-4 right-4 bg-terracotta text-alabaster text-[10px] tracking-[0.25em] uppercase px-3 py-1 rounded-full">
                  {lang === "fr" ? "Recommandé" : "Recommended"}
                </div>
              )}
              <div className={`p-8 lg:p-10 ${idx === 1 ? "bg-cream" : ""}`}>
                <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-3">{c.level}</p>
                <h3 className="font-serif text-4xl text-navy leading-tight mb-2">{lang === "fr" ? c.title_fr : c.title_en}</h3>
                <p className="text-navy/60 italic">{lang === "fr" ? c.tagline_fr : c.tagline_en}</p>

                <div className="flex items-baseline gap-2 mt-8">
                  <span className="font-serif text-5xl text-navy">
                    ${typeof c?.price === "number" ? c.price.toFixed(0) : (c?.price ?? "0")}
                  </span>
                  <span className="text-sm text-navy/60">{c.currency || "CAD"}</span>
                </div>

                <div className="mt-6 flex items-center gap-4 text-xs text-navy/60">
                  <span className="inline-flex items-center gap-1"><Clock size={14} />{c.duration_hours}h</span>
                  <span className="inline-flex items-center gap-1"><Layers size={14} />{c.modules?.length || 0} modules</span>
                </div>

                <p className="mt-6 text-navy/70 text-sm leading-relaxed">{lang === "fr" ? c.description_fr : c.description_en}</p>

                <ul className="mt-6 space-y-2">
                  {(c.modules || []).map((m) => (
                    <li key={`${c.course_id}-${m.title_en}`} className="flex items-start gap-3 text-sm text-navy/80">
                      <Check size={16} className="mt-0.5 text-terracotta shrink-0" />
                      <span>{lang === "fr" ? m.title_fr : m.title_en}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => enroll(c)}
                  disabled={busyId === c.course_id}
                  data-testid={`course-enroll-${c.course_id}`}
                  className={`mt-8 w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-full text-sm transition ${
                    idx === 1
                      ? "bg-terracotta text-alabaster hover:bg-navy"
                      : "bg-navy text-alabaster hover:bg-terracotta"
                  }`}
                >
                  {busyId === c.course_id ? t("common.loading") : t("tcf.buy")}
                  <Sparkles size={14} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ / DETAIL */}
      <section className="bg-navy text-alabaster py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-4">— {lang === "fr" ? "Pourquoi le TCF ?" : "Why TCF?"}</p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight tracking-tight">
              {lang === "fr" ? "Points bonus. Portes ouvertes." : "Bonus points. Open doors."}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-alabaster/80 leading-relaxed">
            <p>{lang === "fr"
              ? "Le TCF Canada est reconnu par IRCC pour l'immigration économique (Entrée Express, PCP) et la citoyenneté. Un score CLB 7+ peut ajouter 25 à 50 points CRS."
              : "TCF Canada is IRCC-recognized for economic immigration (Express Entry, PNP) and citizenship. A CLB 7+ score can add 25-50 CRS points."}
            </p>
            <p>{lang === "fr"
              ? "Notre formation combine théorie, pratique guidée et examens blancs. Josias, francophone natif, coache personnellement les élèves du pack Complet."
              : "Our training combines theory, guided practice, and mock exams. Josias, a native French speaker, personally coaches Complete Pack students."}
            </p>
            <div className="pt-4 border-t border-alabaster/15">
              <p className="text-xs tracking-[0.2em] uppercase text-alabaster/50">{lang === "fr" ? "Avantage CRS moyen" : "Average CRS boost"}</p>
              <p className="font-serif text-6xl text-gold mt-2">+34 pts</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
